import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Truck, ShoppingBag } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import { MENU, TESTIMONIALS } from "@/lib/data";

export default function Home() {
  const featured = [MENU[0], MENU[1], MENU[5]];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] md:min-h-screen flex items-end overflow-hidden grain-overlay">
        <img
          src="/brand-images/image23.jpg"
          alt="TacoSoul food truck at night"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-[#0a0a0a]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-transparent" />

        <div className="relative w-full max-w-7xl mx-auto px-5 md:px-8 pb-16 md:pb-28 pt-32">
          <div className="max-w-3xl">
            <p
              data-testid="hero-eyebrow"
              className="inline-block px-3 py-1.5 border border-[#C99C3A]/60 text-[#C99C3A] text-[11px] tracking-[0.25em] uppercase mb-6 font-medium"
            >
              ★ Charlotte, NC · Afro-Latina Fusion
            </p>
            <h1
              data-testid="hero-headline"
              className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tighter text-[#F5ECD7]"
            >
              Home of{" "}
              <span className="font-serif-italic font-normal text-[#C99C3A]">Deep Fried</span>{" "}
              Tacos.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-[#F5ECD7]/80 max-w-2xl font-light leading-relaxed">
              Charlotte's home for Afro-Latina fusion street food — inspired
              by the flavors of Black and Hispanic culture, served with soul.
            </p>

            <div className="mt-10 flex flex-wrap gap-3 md:gap-4">
              <Link to="/book" className="btn-gold" data-testid="hero-cta-book">
                <Truck size={16} /> Book the Truck
              </Link>
              <Link to="/shop" className="btn-outline" data-testid="hero-cta-shop">
                <ShoppingBag size={16} /> Order Frozen Tacos
              </Link>
              <Link to="/catering" className="btn-outline" data-testid="hero-cta-catering">
                <Calendar size={16} /> Catering
              </Link>
              <Link to="/schedule" className="btn-outline" data-testid="hero-cta-find">
                <MapPin size={16} /> Find the Truck
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "Where Tacos Meet Soul",
          "Deep-Fried Perfection",
          "Charlotte, NC",
          "Saginaw Roots",
          "Bold · Soulful · Crispy",
        ]}
      />

      {/* OUR STORY SNAPSHOT */}
      <section className="section-pad bg-[#121212]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-5">
              Our Story
            </p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-[#F5ECD7] leading-[1.05]">
              From Saginaw{" "}
              <span className="font-serif-italic font-normal text-[#C99C3A]">
                to Charlotte
              </span>
              .
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#F5ECD7]/75 leading-relaxed">
              Growing up in Saginaw, Michigan, Black and Hispanic families ate
              from the same tables. Tortillas folded around grandma's brisket.
              Jerk seasoning passed across the fence. Out of those shared
              kitchens — and one unforgettable deep-fried taco — #TacoSoul was
              born.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#F5ECD7]/65 leading-relaxed">
              We brought those flavors to Charlotte to do one thing: make
              tacos that taste like culture, community, and a Sunday afternoon
              with the people you love.
            </p>
            <Link to="/story" className="mt-8 inline-flex items-center gap-2 text-[#C99C3A] uppercase tracking-[0.2em] text-xs hover:gap-3 transition-all" data-testid="link-read-story">
              Read the full story <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={150} className="md:col-span-6 order-1 md:order-2">
            <div className="relative overflow-hidden rounded-2xl aspect-[4/5] md:aspect-[5/6]">
              <img src="/brand-images/image14.jpg" alt="TacoSoul food truck" className="w-full h-full object-cover" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[#F5ECD7] text-xs uppercase tracking-[0.2em]">
                <span>Est. 2023</span>
                <span className="text-[#C99C3A]">Charlotte, NC</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED MENU */}
      <section className="section-pad bg-[#0e0e0e]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-4">The Menu</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter">
                Crispy on the outside.
                <br />
                <span className="font-serif-italic font-normal text-[#C99C3A]">Soul on the inside.</span>
              </h2>
            </div>
            <Link to="/menu" className="btn-outline self-start" data-testid="link-full-menu">
              See Full Menu <ArrowRight size={14} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {featured.map((item, i) => (
              <Reveal key={item.id} delay={i * 120} className="group">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-[#1A1A1A]">
                  <img src={item.image} alt={item.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-[#C99C3A] text-xs tracking-[0.25em] uppercase mb-2">{item.tags[0] || "Signature"}</p>
                    <h3 className="font-display text-2xl md:text-3xl text-[#F5ECD7] tracking-tight">{item.name}</h3>
                    <p className="text-[#F5ECD7]/70 text-sm mt-1 line-clamp-2">{item.blurb}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIND THE TRUCK */}
      <section className="section-pad bg-[#F5ECD7] text-[#121212]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#9B2226] mb-4">Find the Truck</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[1.05]">
              Tonight you'll find us under the
              <span className="font-serif-italic font-normal"> string lights</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#121212]/70 max-w-xl">
              Check the weekly schedule, find festivals near you, and get
              directions in one tap.
            </p>
            <Link to="/schedule" className="mt-8 inline-flex btn-red" data-testid="link-schedule">
              See This Week's Schedule <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={150} className="md:col-span-5">
            <div className="bg-[#121212] text-[#F5ECD7] p-7 md:p-9 rounded-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C99C3A] mb-3">Catering</p>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight">Feed your event.</h3>
              <p className="text-[#F5ECD7]/70 mt-2">Weddings · Corporate · Festivals · Private parties · 25–500+ guests.</p>
              <div className="mt-6 pt-6 border-t border-[#F5ECD7]/10 flex items-center justify-between">
                <span className="font-display text-xl">Custom Menus</span>
                <Link to="/catering" className="text-[#C99C3A] uppercase text-xs tracking-[0.2em] hover:underline" data-testid="link-catering-inline">Get a Quote →</Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150} className="md:col-span-5">            <div className="bg-[#121212] text-[#F5ECD7] p-7 md:p-9 rounded-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C99C3A] mb-3">Tonight</p>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight">Resident Culture Brewing</h3>
              <p className="text-[#F5ECD7]/70 mt-2">2820 South Blvd · South End, Charlotte</p>
              <div className="mt-6 pt-6 border-t border-[#F5ECD7]/10 flex items-center justify-between">
                <span className="font-display text-xl">5:00 – 9:00 PM</span>
                <a
                  href="https://maps.google.com/?q=Resident+Culture+Brewing+Charlotte"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#C99C3A] uppercase text-xs tracking-[0.2em] hover:underline"
                  data-testid="link-directions"
                >
                  Get Directions →
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#121212]">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-4 text-center">Word on the Street</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-center">
              People are <span className="font-serif-italic font-normal text-[#C99C3A]">talking</span>.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.slice(0, 4).map((t, i) => (
              <Reveal key={i} delay={i * 100} className="bg-[#1A1A1A] border border-[#F5ECD7]/5 p-8 md:p-10 rounded-2xl">
                <p className="font-serif text-xl md:text-2xl text-[#F5ECD7] leading-snug">"{t.quote}"</p>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <span className="text-[#C99C3A] font-medium">{t.name}</span>
                  <span className="text-[#F5ECD7]/50 text-xs uppercase tracking-[0.2em]">{t.role}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
